import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ISiteMapData } from '@interfaces/ISiteMap';
import {
  clearSiteMap, deleteSiteMapArea, deleteSiteMapEquipment, fetchSiteMap,
  patchSiteMapArea, patchSiteMapEquipment, postSiteMapArea, postSiteMapEquipment,
  putSiteMapPlan,
} from '@apis/siteMap';
import { isStaleRefusal } from '@apis/logisticsRevision';
import { useLogisticsEditLock } from '../../revision/LogisticsEditLock';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

/**
 * `onError` — surfaced by the caller as a toast (`SiteMap.tsx` threads
 * LogisticsPage's own `push`), matching the page's convention elsewhere
 * (`useLogisticsMutations.ts`): every write gets an error toast, not a
 * silent failure. One generic message covers all eight writes — the failure
 * mode (network, 400, 404) is the same story for the user in every case.
 */
export function useSiteMapData(siteId: string, onError?: (message: string) => void) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { revision } = useLogisticsEditLock();
  const siteMapKey = ['site-map', siteId] as const;
  const invalidate = () => qc.invalidateQueries({ queryKey: siteMapKey });
  // A stale refusal is the refresh dock's to announce, not a toast's.
  const notifyError = (error: Error) => {
    if (!isStaleRefusal(error)) onError?.(t('logistics.siteMap.saveError'));
  };
  const mapQuery = useQuery({
    queryKey: siteMapKey,
    queryFn: ({ signal }) => fetchSiteMap(siteId, signal),
    enabled: Boolean(siteId),
  });
  return {
    mapQuery,
    setPlan: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (floorId: string) => putSiteMapPlan(siteId, floorId, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    createArea: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (body: Parameters<typeof postSiteMapArea>[1]) => postSiteMapArea(siteId, body, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    updateArea: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (v: { areaId: string; patch: Parameters<typeof patchSiteMapArea>[2] }) =>
        patchSiteMapArea(siteId, v.areaId, v.patch, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    removeArea: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (areaId: string) => deleteSiteMapArea(siteId, areaId, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    createEquipment: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (body: Parameters<typeof postSiteMapEquipment>[1]) => postSiteMapEquipment(siteId, body, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    // Optimistic: a drag's pointerup PATCHes, but the invalidated refetch
    // lands a beat later — without patching the cache synchronously here, the
    // chip/circle would render the SAVED (pre-drag) value in that gap and
    // visibly snap back before the real data arrives. `onMutate` applies the
    // same patch straight to the query cache; `onError` rolls it back before
    // the shared error toast fires; `onSettled` still reconciles with the
    // server either way (success or rollback).
    updateEquipment: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (v: { equipmentId: string; patch: Parameters<typeof patchSiteMapEquipment>[2] }) =>
        patchSiteMapEquipment(siteId, v.equipmentId, v.patch, revision),
      onMutate: async (v) => {
        await qc.cancelQueries({ queryKey: siteMapKey });
        const previous = qc.getQueryData<ISiteMapData>(siteMapKey);
        if (previous) {
          qc.setQueryData<ISiteMapData>(siteMapKey, {
            ...previous,
            equipment: previous.equipment.map((eq) =>
              eq.equipmentId === v.equipmentId ? { ...eq, ...v.patch } : eq,
            ),
          });
        }
        return { previous };
      },
      onError: (error, _v, context) => {
        if (context?.previous) qc.setQueryData(siteMapKey, context.previous);
        notifyError(error);
      },
      onSettled: invalidate,
    }),
    removeEquipment: useMutation({
      meta: OWNS_ERROR_FEEDBACK,
      mutationFn: (id: string) => deleteSiteMapEquipment(siteId, id, revision),
      onSuccess: invalidate,
      onError: notifyError,
    }),
    clearMap: useMutation({ meta: OWNS_ERROR_FEEDBACK, mutationFn: () => clearSiteMap(siteId, revision), onSuccess: invalidate, onError: notifyError }),
  };
}
